import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { callAll, RQ_GLOBAL_RULES, RQ_RULE_GROUPS } from 'Utils';
import WarningModalContent from 'Components/ConcurrencyHelpers/WarningModalContent';
import { getTranslationErrorHandling } from '../helper';

type Options = {
    mode: 'overview' | 'rule';
    // required for rule type
    ruleId?: string;
};

/**
 * You might be wondering why I didn't create a generic abstraction for all error handling that handles
 * the different levels (MERCHANT, ACQUIRER, MCC) and the reason is pretty simple:
 * I'm doing AHA programming -- avoiding a hasty abstraction.
 * https://kentcdodds.com/blog/aha-programming
 *
 * There's nothing wrong with copying pastaing a file two times. Better that we have something that's readable
 * and easy to reason about than a highly elusive super flexible hook that does everything
 */

function useGlobalRuleErrorHandler(repoId: number | null, { mode, ruleId }: Options) {
    const { t } = useTranslation();
    const globalRulesStringKey = getTranslationErrorHandling(t, 'globalRules');
    const errorStringKey = getTranslationErrorHandling(t);

    const { addTechnicalErrorModal } = useReduxModal();
    const queryClient = useQueryClient();
    const isOverview = mode === 'overview';
    const modalTitle = globalRulesStringKey(isOverview ? 'overviewTitle' : 'ruleTitle');

    const errorHandler = React.useCallback(
        (error: any, cb: () => void) => {
            const invalidateQuery = () => {
                queryClient.invalidateQueries([RQ_GLOBAL_RULES, repoId]);
                queryClient.invalidateQueries([RQ_RULE_GROUPS]);
            };
            const onModalCloseInvalidateQueryWithCb = callAll(invalidateQuery, cb);

            const handleConflict = () => {
                addTechnicalErrorModal('', modalTitle, onModalCloseInvalidateQueryWithCb, {
                    status: 'warning',
                    content: (
                        <WarningModalContent
                            title={
                                isOverview
                                    ? globalRulesStringKey('overviewPreamble')
                                    : errorStringKey('rulePreamble', { ruleId })
                            }
                            subTitle={errorStringKey('retry')}
                        />
                    )
                });
            };

            const handleDefault = () => {
                const preContentMessage = isOverview
                    ? globalRulesStringKey('genericMessage')
                    : errorStringKey('ruleFailed', { ruleId });
                addTechnicalErrorModal(preContentMessage, errorStringKey('unableToSave'), cb);
            };

            switch (error?.status) {
                case 409:
                    handleConflict();
                    break;
                default:
                    handleDefault();
                    break;
            }
        },
        [
            queryClient,
            repoId,
            addTechnicalErrorModal,
            modalTitle,
            isOverview,
            globalRulesStringKey,
            errorStringKey,
            ruleId
        ]
    );

    return errorHandler;
}

export default useGlobalRuleErrorHandler;
